import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useHistory, useParams } from "react-router-dom";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { Fragment, useMemo, useRef, useState } from "react";
import { Button, FormTagsComponent, FormTextField, HrLine, Text } from "@ting/ting-ui-components";
import { getTimezone } from "countries-and-timezones";
import { DateTimeMeeting } from "./DateTimeMeeting";
import { useDisplayNameOptions, DisplayNameSelect } from "@src/components/DisplayName";
import { Header, HeaderSizes } from "@src/components/Header";
import { ScheduleMeetingMessageModal } from "@src/components/ScheduleMeetingMessageModal";
import { classNameFactory } from "@src/utils/dom";
import { useBuildMobile } from "@src/hooks/breakpoints";
import { Container } from "@src/components/Container";
import { ToggleWithContent } from "@src/components/ToggleWithContent";
import { PremiumTag } from "@src/components/PremiumTag";
import { createPublicRoom, createScheduleMeeting } from "@src/controller";
import { routePaths } from "@src/routes/routes";
import { RoomMode, RoomType } from "@src/domain/Room";
import { DaysOfWeek, Recurrences } from "@src/services/Calendar/enums";
import { addHoursToTime } from "@src/utils/date";
import "./createRoom.scss";
const cn = classNameFactory("create-room");
export const CreateRoom = () => {
    const { t } = useTranslation(["common", "room"]);
    const isBuildMobile = useBuildMobile();
    const [scheduleMessageModal, setScheduleMessageModal] = useState(false);
    const [displayNameValues, setVerifiedName] = useDisplayNameOptions();
    const [isLoading, setLoading] = useState(false);
    const history = useHistory();
    const scheduleRoomId = useRef();
    const params = useParams();
    const isScheduled = params?.type === RoomType.scheduled;
    const defaultTimezone = useMemo(() => {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const currentTimezone = getTimezone(timezone);
        // TODO: write a func
        return {
            label: `${currentTimezone.name} - UTC${currentTimezone.utcOffsetStr}`,
            value: currentTimezone.name,
        };
    }, []);
    const onCancelClick = () => history.push(routePaths.HOME());
    const onFormSubmit = async (formValues) => {
        try {
            setLoading(true);
            const roomId = await createPublicRoom({
                title: formValues.meetingName,
                // TODO: save name somewhere so that we can use it in lobby
                // name: displayNameValues[formValues.displayName],
                mode: formValues.conferenceMode ? RoomMode.conference : RoomMode.normal,
                lobbyEnabled: formValues.lobby,
                locked: formValues.lockMeeting,
                entryMic: formValues.participantsMic,
                entryCam: formValues.participantsVideo,
                type: isScheduled ? RoomType.public : RoomType.instant,
            });
            if (isScheduled) {
                scheduleRoomId.current = roomId;
                const origin = window.location.origin.toString();
                const roomLink = `${origin}/room/${roomId}`;
                await createScheduleMeeting({
                    title: formValues.meetingName,
                    endDate: formValues.endDate,
                    startDate: formValues.startDate,
                    startTime: formValues.startTime,
                    endTime: formValues.endTime,
                    timeZone: formValues.timezone.value,
                    monthlyOccursOn: formValues.monthlyOccursOn,
                    recurrence: formValues.recurrence,
                    recurrenceEndDate: formValues.recurrenceEndDate,
                    weeklyOccursOn: formValues.weeklyOccursOn,
                    link: roomLink,
                    attendees: formValues.attendees.map(val => ({ email: val })),
                });
                setScheduleMessageModal(true);
            }
            else {
                history.push(routePaths.LOBBY(roomId));
            }
        }
        catch (e) {
            // console.log(e, "e");
        }
        finally {
            setLoading(false);
        }
    };
    const initialValues = {
        meetingName: "",
        displayName: Object.keys(displayNameValues)[2],
        lockMeeting: false,
        lobby: false,
        conferenceMode: false,
        participantsMic: true,
        participantsVideo: true,
        // SCHEDULED
        timezone: defaultTimezone,
        recurrence: Recurrences.none,
        startDate: new Date(),
        startTime: new Date(),
        endDate: new Date(),
        endTime: addHoursToTime(new Date(), 1),
        weeklyOccursOn: [DaysOfWeek.Mon],
        recurrenceEndDate: new Date(),
        monthlyOccursOn: "1",
        attendees: [],
    };
    return (_jsxs("div", { className: cn(""), children: [_jsx(Container, { className: cn("header"), children: _jsx(Header, { size: isBuildMobile ? HeaderSizes.medium : HeaderSizes.big, children: isScheduled ? t("room:schedule-meeting") : t("room:instant-meeting") }) }), _jsx(HrLine, { className: cn("divider") }), _jsx(Formik, { initialValues: initialValues, onSubmit: onFormSubmit, children: ({ handleSubmit, setFieldValue, values, setFieldTouched }) => (_jsxs(Fragment, { children: [_jsxs(Container, { className: cn("content"), children: [_jsx(FormTextField, { v2: true, name: "meetingName", label: t("room:meeting-name"), maxLength: 100, size: "large", className: cn("input") }), _jsx(DisplayNameSelect, { value: values.displayName, options: displayNameValues, name: "displayName", onChange: value => setFieldValue("displayName", value), className: cn("input"), onChangeVerifiedName: setVerifiedName }), _jsx(HrLine, { className: cn("divider") }), isScheduled && (_jsxs(Fragment, { children: [_jsx(DateTimeMeeting, {}), _jsx(HrLine, { className: cn("divider") })] })), isScheduled && (_jsxs(_Fragment, { children: [_jsxs("div", { className: cn("attendees"), children: [_jsx(Text, { size: "p2", fontWeight: "medium", children: t("attendees") }), _jsx(FormTagsComponent, { name: "attendees", placeholderText: "", value: values.attendees.map((v, i) => ({ name: v, id: i })), onBlur: () => {
                                                        setFieldTouched("attendees", true);
                                                    }, setFieldValue: (name, val) => {
                                                        setFieldTouched(name, true);
                                                        setFieldValue(name, val.map(v => v.name));
                                                    } })] }), _jsx(HrLine, { className: cn("divider") })] })), _jsx(Header, { size: HeaderSizes.small, children: t("room:meeting-settings") }), _jsxs("div", { className: cn("meeting-setting"), children: [_jsx(ToggleWithContent, { value: values.lockMeeting, name: "lockMeeting", label: t("room:lock-meeting"), caption: t("room:lock-meeting-desc"), labelExtra: _jsx(PremiumTag, {}), onClick: e => setFieldValue("lockMeeting", e) }), _jsx(ToggleWithContent, { value: values.lobby, name: "lobby", label: t("room:lobby"), caption: t("room:lobby-desc"), onClick: e => setFieldValue("lobby", e) }), _jsx(ToggleWithContent, { value: values.conferenceMode, name: "conferenceMode", label: t("room:conference-mode"), caption: t("room:conference-mode-desc"), labelExtra: _jsx(PremiumTag, {}), onClick: e => setFieldValue("conferenceMode", e) })] }), _jsx(HrLine, { className: cn("divider") }), _jsx(Header, { size: HeaderSizes.small, children: t("room:mic-and-video-upon-entry") }), _jsxs("div", { className: cn("participant-setting"), children: [_jsx(ToggleWithContent, { value: values.participantsMic, name: "participantsMic", label: t("room:participants-mic"), onClick: e => setFieldValue("participantsMic", e) }), _jsx(ToggleWithContent, { value: values.participantsVideo, name: "participantsVideo", label: t("room:participants-video"), onClick: e => setFieldValue("participantsVideo", e) })] })] }), _jsxs("div", { className: cn("footer"), children: [!isBuildMobile && _jsx(HrLine, { className: cn("divider") }), _jsxs(Container, { className: cn("footer-container"), children: [_jsx(Button, { v2: true, size: "big", type: "neutral", className: cn("footer-btn"), onClick: onCancelClick, children: t("cancel") }), _jsx(Button, { isLoading: isLoading, v2: true, size: "big", type: "brand", className: cn("footer-btn"), onClick: handleSubmit, children: isScheduled ? t("room:schedule-meeting") : t("continue") })] })] })] })) }), isScheduled && scheduleMessageModal && (_jsx(ScheduleMeetingMessageModal, { roomId: scheduleRoomId.current, open: scheduleMessageModal, onClose: () => setScheduleMessageModal(false) }))] }));
};
